import { configService } from '@/services/configService.js';
import { authTokenService } from '@/services/authTokenService.js';

export const utilityService = {
	getResearcherApiUrl: (urlFragment) => __getResearcherApiUrl(urlFragment),
	getAnalysisApiUrl: (urlFragment) => __getAnalysisApiUrl(urlFragment),
	getApiAuthUrl: (urlFragment) => __getApiAuthUrl(urlFragment),
	convertStringToNum: (val) => _convertStringToNum(val),
	scrollElemIntoView: (id) => __scrollElementIntoView(id),
	getTimeStamp: () => __getTimeStamp(),
	getLoginUrl: (includeCallingFrom = true) => __getLoginUrl(includeCallingFrom),
	getDemoUrl: () => __getDemoUrl(),
	getRespondentUiUrl: () => __getRespondentUiUrl(),
	getEnv: () => __getEnv(),
	getDisplayLoginForm: () => __getDisplayLoginForm(),
	getDisplayRegisterButton: () => __getDisplayRegisterButton(),
	getHeaders: (includeBearer) => __getHeaders(includeBearer),
	getTabsPos: () => __getTabsPos(),
	clearTabsPos: () => __clearTabsPos(),
	getTabsPositionByName: (subtabName, studyTabConfig) => __getTabsPositionByName(subtabName, studyTabConfig),
	formatDate: (date) => __formatDate(date),
	formatDateTime: (date) => __formatDateTime(date),
	normalizeEpoch: (epoch) => __normalizeEpoch(epoch),
	//
	getDisplayRegister: () => __getDisplayRegister(),
	getRegisterUrl: () => __getRegisterUrl(),
	getRegisterHostingMessage: () => __getRegisterHostingMessage(),
	copyToClipboard: (text) => __copyToClipboard(text)
};

const baseResearcherApiUrl = configService.researcher_api_url;
const baseAnalysisAPIUrl = configService.analysis_api_url;
const baseApiAuthUrl = configService.api_auth_url;
const loginUrl = configService.login_url;
const demoUrl = configService.demo_url;
const respondentUiUrl = configService.ui_respondent_url;
const env = configService.env;
const displayLoginForm = configService.config_display === 'true';
const displayRegisterButton = configService.display_register === 'true';
const commaRegex = /,/gi;
const displayRegister = configService.display_register === 'true';
const registerUrl = configService.register_url;
const registerHostingMessage = configService.register_hosting_message === 'true';

function __trimTrailingSlash (url) {
	if (url.length === 0) {
		return url;
	}

	return (url.substr(-1) === '/') ? url.slice(0, -1) : url;
}

function __trimLeadingSlash (url) {
	if (url.length === 0) {
		return url;
	}

	return (url.charAt(0) === '/') ? url.substring(1, url.length) : url;
}

/**
 * Appends the url fragment to the api url base.
 * @param {string} urlFragment - A url fragment to append to the api url base.
 */
function __getResearcherApiUrl (urlFragment) {
	return __trimTrailingSlash(baseResearcherApiUrl) + '/' + __trimLeadingSlash(urlFragment.trim());
}

function __getAnalysisApiUrl (urlFragment) {
	return __trimTrailingSlash(baseAnalysisAPIUrl) + '/' + __trimLeadingSlash(urlFragment.trim());
}

function __getApiAuthUrl (urlFragment) {
	return __trimTrailingSlash(baseApiAuthUrl) + '/' + __trimLeadingSlash(urlFragment.trim());
}

function __getLoginUrl (includeCallingFrom = true) {
	let url = loginUrl;

	if (includeCallingFrom) {
		url += '?CallingFrom=ResearcherSite';
	}

	return url;
}

function __getDemoUrl () {
	return demoUrl;
}

function __getRespondentUiUrl () {
	return __trimTrailingSlash(respondentUiUrl);
}

function __getEnv () {
	return env;
}

function __getDisplayLoginForm () {
	return displayLoginForm;
}

function __getDisplayRegisterButton () {
	return displayRegisterButton;
}

function _convertStringToNum (val) {
	if (!val) return 0;

	if (typeof (val) === 'string') return parseInt(val.replace(commaRegex, ''), 10);

	return val;
}

function __scrollElementIntoView (id) {
	if (id) {
		document.getElementById(id).scrollIntoView();
	}
}

/**
 * Gets a timestamp in a user readable format.
 */
function __getTimeStamp () {
	const currentDatetime = new Date();
	console.debug(currentDatetime.toString());
	const formattedDate =
		currentDatetime.getFullYear() +
		'-' +
		__appendLeadingZeroes(currentDatetime.getMonth() + 1) +
		'-' +
		__appendLeadingZeroes(currentDatetime.getDate()) +
		' ' +
		__appendLeadingZeroes(currentDatetime.getHours()) +
		':' +
		__appendLeadingZeroes(currentDatetime.getMinutes()) +
		':' +
		__appendLeadingZeroes(currentDatetime.getSeconds());
	return formattedDate;
}

/**
 * Appends leading zeroes to the number if it is a single digit.
 * @param {Number} n - The number to pad.
 */
function __appendLeadingZeroes (n) {
	if (n <= 9) {
		return '0' + n;
	}
	return n;
}

/**
 * Gets the headers for a web service call. If a token is available it is added to the headers.
 */
function __getHeaders (includeBearer = true) {
	const headers = {
		'Content-Type': 'application/json'
	};
	const token = authTokenService.getToken();

	if (token) {
		if (includeBearer) {
			headers.Authorization = 'Bearer ' + token;
		} else {
			headers.Authorization = token;
		}
	}

	return headers;
}

/**
 * Get the tab and subtab position from session storage
 */
async function __getTabsPos () {
	var tabsPos = {
		tab: sessionStorage.getItem('tabPos'),
		subtab: sessionStorage.getItem('subtabPos')
	}

	return Promise.resolve(tabsPos);
}

/**
 * Clears the tab and subtab postiion in session storage
 */
function __clearTabsPos () {
	sessionStorage.removeItem('tabPos');
	sessionStorage.removeItem('subtabPos');
}

/**
 * Return the position of the tabs based on the name of the subtab
 */
async function __getTabsPositionByName (subtabName, studyTabConfig) {
	for (var i = 0; i < studyTabConfig.length; i++) {
		if (!studyTabConfig[i].subTabs.length) {
			return Promise.resolve({ tabPos: i, subtabPos: null });
		}
		for (var j = 0; j < studyTabConfig[i].subTabs.length; j++) {
			if (subtabName.toLowerCase() === studyTabConfig[i].subTabs[j].tabName.toLowerCase()) {
				return Promise.resolve({ tabPos: i, subtabPos: j });
			}
		}
	}

	return null;
}

/**
 * Changes date formating from <date>T<time>Z to <date> only
 * @param {*} date string in date format
 */
function __formatDate (date) {
	const d = new Date(date);
	// Get month as a two digit string
	let month = (d.getMonth() + 1).toString();
	if (month.length < 2) {
		month = '0' + month;
	}
	// Get day as two day string
	let day = d.getDate().toString();
	if (day.length < 2) {
		day = '0' + day;
	}

	return d.getFullYear() + '-' + month + '-' + day;
}

const __localDateStringOptions = {
	year: 'numeric',
	month: '2-digit',
	day: '2-digit',
	timeZoneName: 'shortGeneric',
	hour: '2-digit',
	minute: '2-digit',
	second: '2-digit'
};

/**
 * Formats the date string to our preferred date time format.
 * @param {*} date - The date string to format.
 */
function __formatDateTime (date) {
	const d = new Date((date || '').replace('UTC', 'Z'));
	const formattedDate = d.toLocaleDateString([], __localDateStringOptions);

	// Get month as a two digit string
	let month = (d.getMonth() + 1).toString();
	if (month.length < 2) {
		month = '0' + month;
	}
	// Get day as two day string
	let day = d.getDate().toString();
	if (day.length < 2) {
		day = '0' + day;
	}

	let dateTime = d.getFullYear() + '-' + month + '-' + day;
	const time = formattedDate.split(',');
	if (time && time.length > 1) {
		dateTime += ' ' + time[1].trim();
	}
	return dateTime;
}

/**
 * Normalizes the date epoch to a milliseconds representation.
 * @param {*} epoch - A value representing the date in epoch format.
 * @returns The date epoch in milliseconds.
 */
function __normalizeEpoch (epoch) {
	let epochNormalized = null;

	if (epoch) {
		const epochString = epoch.toString();
		// Assumptions based on the oldest study date being from 2014
		if (epochString.length < 13) {
			// Assume epoch is in seconds
			epochNormalized = parseInt(epoch) * 1000;
		} else {
			// Assume milliseconds
			epochNormalized = parseInt(epoch);
		}
	}

	return epochNormalized;
}

function __getDisplayRegister () {
	return displayRegister;
}

function __getRegisterUrl () {
	return registerUrl;
}

function __getRegisterHostingMessage () {
	return registerHostingMessage;
}

/**
 * Copies the text to the clipboard.
 * @param {*} text - The text to copy to the clipboard.
 * @returns A promise that resolves to the text copied to the clipboard.
 */
function __copyToClipboard(text) {
	return navigator.clipboard.writeText(text);
}
